import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, type Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID as string,
};

function getFirebaseApp(): FirebaseApp {
  return getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
}

function isFirebaseConfigured(): boolean {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId
  );
}

/**
 * Request notification permission and return the FCM registration token.
 * Returns null if permission is denied, the browser doesn't support it,
 * or the Firebase web config env vars are not set.
 */
export async function requestFcmToken(): Promise<string | null> {
  if (!isFirebaseConfigured()) {
    return null;
  }

  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return null;
    }

    const swParams = new URLSearchParams({
      apiKey:            firebaseConfig.apiKey,
      authDomain:        firebaseConfig.authDomain,
      projectId:         firebaseConfig.projectId,
      storageBucket:     firebaseConfig.storageBucket,
      messagingSenderId: firebaseConfig.messagingSenderId,
      appId:             firebaseConfig.appId,
    });

    const swRegistration = await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?${swParams.toString()}`,
    );

    const app = getFirebaseApp();
    const messaging: Messaging = getMessaging(app);

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

    return await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });
  } catch (err) {
    console.warn('[FCM] requestFcmToken failed:', err);
    return null;
  }
}
